import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import FormHelperText from '@mui/material/FormHelperText';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { Seo } from 'src/components/seo';
import { useMounted } from 'src/hooks/use-mounted';
import { useRouter } from 'src/hooks/use-router';
import { paths } from 'src/paths';
import authService from 'src/services/apiServices/authService';
// release_5.21.0 has no src/utils/get-realm-name (a develop-only refactor splitting getRealmName
// out of AuthContext to avoid its Keycloak-init side effect on import) — use AuthContext's own
// export instead, which is functionally identical and already what app.tsx imports.
import { getRealmName } from 'src/routes/AuthContext';

interface Values {
  email: string;
  submit: null;
}

const initialValues: Values = {
  email: '',
  submit: null,
};

const validationSchema = Yup.object({
  email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
});

// APIT-696 AC 4-6: user-service always returns the same response whether or not the email
// belongs to an account. This copy is shown for that success response AND for any failure the
// request itself hits (network error, unexpected 4xx/5xx) — never let the two diverge, or the
// difference itself reveals account existence.
export const FORGOT_PASSWORD_CONFIRMATION =
  "If an account exists for that email, we've sent a link to reset your password.";

const Page = () => {
  const isMounted = useMounted();
  const router = useRouter();
  const [submitted, setSubmitted] = useState(false);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, helpers): Promise<void> => {
      try {
        await authService.forgotPassword({
          email: values.email,
          subdomain: getRealmName() || '',
        });
      } catch (err) {
        // Swallowed deliberately — see FORGOT_PASSWORD_CONFIRMATION above. Logged for our own
        // diagnostics only; the user always sees the same neutral message.
        console.error(err);
      } finally {
        if (isMounted()) {
          helpers.setSubmitting(false);
          setSubmitted(true);
        }
      }
    },
  });

  return (
    <>
      <Seo title="Forgot Password" />
      <div>
        <Card elevation={16}>
          <CardHeader
            sx={{ pb: 0 }}
            title="Forgot password"
          />
          <CardContent>
            {submitted ? (
              <>
                <Typography>{FORGOT_PASSWORD_CONFIRMATION}</Typography>
                <Link
                  // Full page load, not a router link: Keycloak isn't initialised on this page
                  // (AuthContext skips it), so only a fresh boot can redirect to the realm login.
                  href={paths.auth.login}
                  sx={{ mt: 3, display: 'inline-block' }}
                  underline="hover"
                  variant="subtitle2"
                >
                  Back to login
                </Link>
              </>
            ) : (
              <form
                noValidate
                onSubmit={formik.handleSubmit}
              >
                <Typography
                  color="text.secondary"
                  sx={{ mb: 3 }}
                  variant="body2"
                >
                  Enter the email address associated with your account and we&apos;ll send you a
                  link to reset your password.
                </Typography>
                <TextField
                  autoFocus
                  error={!!(formik.touched.email && formik.errors.email)}
                  fullWidth
                  helperText={formik.touched.email && formik.errors.email}
                  label="Email Address"
                  name="email"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type="email"
                  value={formik.values.email}
                />
                {formik.errors.submit && (
                  <FormHelperText
                    error
                    sx={{ mt: 3 }}
                  >
                    {formik.errors.submit as string}
                  </FormHelperText>
                )}
                <Button
                  disabled={formik.isSubmitting}
                  fullWidth
                  size="large"
                  sx={{ mt: 3 }}
                  type="submit"
                  variant="contained"
                >
                  Send reset link
                </Button>
                <Link
                  // Full page load, not a router link: Keycloak isn't initialised on this page
                  // (AuthContext skips it), so only a fresh boot can redirect to the realm login.
                  href={paths.auth.login}
                  sx={{ mt: 2, display: 'block', textAlign: 'center' }}
                  underline="hover"
                  variant="subtitle2"
                >
                  Back to login
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default Page;
